// 他参加者のキャレット・選択帯を「図形本体を原点とする相対座標」で扱うための純粋関数群。
//
// canvas-remote-carets-layer.tsx は DOM の実測だけを担い、座標変換・枠への切り詰め・交差判定は
// すべてここへ閉じ込める（DOM に依存しないため境界条件を単体テストで検証できる）。
//
// 座標系の約束:
// - ScreenRect は getBoundingClientRect / getClientRects が返す画面 px（ステージの scale 済み）。
// - ObjectLocalRect は図形本体の左上を原点とするステージ座標 px（scale を割り戻した値）。
// - getComputedStyle が返す長さは CSS px（＝ステージ単位）であり、画面 px とは倍率ぶん食い違う。
//   両者を足し引きするときは、必ずどちらかへそろえてから計算する。

import { CANVAS_WIDTH_PX } from "../../lib/canvas-coordinates";

/** client 座標の矩形（getBoundingClientRect / getClientRects の必要部分）。 */
export interface ScreenRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/** 図形本体を原点にした、ステージ座標（px）の相対矩形。 */
export interface ObjectLocalRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** 切り詰め先の枠（ステージ座標、図形本体の左上を原点とする）。 */
export interface ClampBox {
  readonly width: number;
  readonly height: number;
}

/** キャレットの縦バー（幅 0 の相対矩形）。 */
export interface LocalCaret {
  readonly x: number;
  readonly y: number;
  readonly height: number;
}

/** 縦バーの画面幅（px）。canvas-remote-carets-layer の描画幅と一致させる。 */
export const REMOTE_CARET_BAR_SCREEN_PX = 2;

function isPositiveFinite(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * ステージ要素の実測幅から DOM 上の実倍率を求める。
 *
 * ステージ要素は `width: CANVAS_WIDTH_PX` に `scale(zoom)` を掛けているため、実測幅と基準幅の比が
 * そのまま DOM 上の倍率になる。store の `view.zoom` は「これから描く倍率」であり、実測時点の DOM の
 * 変形とは一瞬食い違うことがあるため、実測できる場合はこちらを優先する。
 * 実測できない（要素が無い / 幅 0 / 非有限）ときは fallback（store の `view.zoom`）を返す。
 * fallback も倍率として使えない場合だけ、0 除算を避けるため 1 とする。
 */
export function resolveStageZoom(stageRectWidth: number | undefined, fallback: number): number {
  if (stageRectWidth !== undefined && isPositiveFinite(stageRectWidth)) {
    const zoom = stageRectWidth / CANVAS_WIDTH_PX;
    if (isPositiveFinite(zoom)) return zoom;
  }
  return isPositiveFinite(fallback) ? fallback : 1;
}

/** 画面矩形を、図形本体の矩形を原点とするステージ座標の相対矩形へ変換する。 */
export function toObjectLocalRect(
  rect: ScreenRect,
  rootRect: ScreenRect,
  zoom: number
): ObjectLocalRect {
  const divisor = isPositiveFinite(zoom) ? zoom : 1;
  return {
    x: (rect.left - rootRect.left) / divisor,
    y: (rect.top - rootRect.top) / divisor,
    width: rect.width / divisor,
    height: rect.height / divisor,
  };
}

/**
 * キャレット（幅 0 の相対矩形）を枠の内側へ切り詰める。
 *
 * 縦バー幅は `min(barWidth, box.width)` に縮めたうえで、横は `[0, width - 縦バー幅]`、
 * 縦は `[0, height - caretHeight]` へ寄せ、縦バーの高さが枠の高さを超えるときは高さを枠に合わせる。
 * 枠が 0 以下、または入力が非有限のときは null（描かない）。
 */
export function clampCaretToBox(
  caret: LocalCaret,
  box: ClampBox,
  barWidth: number
): LocalCaret | null {
  if (!isPositiveFinite(box.width) || !isPositiveFinite(box.height)) return null;
  if (!Number.isFinite(caret.x) || !Number.isFinite(caret.y) || !Number.isFinite(caret.height)) {
    return null;
  }
  const bar = clamp(Number.isFinite(barWidth) ? barWidth : 0, 0, box.width);
  const height = clamp(caret.height, 0, box.height);
  return {
    x: clamp(caret.x, 0, box.width - bar),
    y: clamp(caret.y, 0, Math.max(0, box.height - height)),
    height,
  };
}

/** 選択帯の相対矩形と枠の交差を返す。交差が無ければ null。 */
export function clipRectToBox(rect: ObjectLocalRect, box: ClampBox): ObjectLocalRect | null {
  if (!isPositiveFinite(box.width) || !isPositiveFinite(box.height)) return null;
  const left = Math.max(rect.x, 0);
  const top = Math.max(rect.y, 0);
  const right = Math.min(rect.x + rect.width, box.width);
  const bottom = Math.min(rect.y + rect.height, box.height);
  const width = right - left;
  const height = bottom - top;
  if (!isPositiveFinite(width) || !isPositiveFinite(height)) return null;
  return { x: left, y: top, width, height };
}

/**
 * キャレットの左端（画面 px）に、その行の本文が始まる位置を下限として与える。
 *
 * ぶら下げインデントのある行（箇条書き・番号付きリスト）では、行要素の border box の左端が
 * マーカー領域の左端であり、本文は `padding-left` ぶん右から始まる。実測がマーカー領域や
 * その左へ落ちたときだけ、本文の開始位置へ寄せる。上限は与えない（枠の切り詰めに委ねる）。
 *
 * `lineRect` は画面 px、`paddingLeftCssPx` は getComputedStyle が返す CSS px（ステージ単位）で
 * 単位が異なるため、`zoom` を掛けてからそろえる。
 * `paddingLeftCssPx` が非有限・0 以下、`zoom` が非有限・0 以下、`lineRect` の幅と高さがともに 0 の
 * いずれかなら、そのまま `caretLeftScreenPx` を返す。
 * 求めた下限が行要素の右端を超える場合は、右端で頭打ちにする。
 */
export function clampCaretLeftToLineContent(
  caretLeftScreenPx: number,
  lineRect: ScreenRect,
  paddingLeftCssPx: number,
  zoom: number
): number {
  if (!isPositiveFinite(paddingLeftCssPx)) return caretLeftScreenPx;
  if (!isPositiveFinite(zoom)) return caretLeftScreenPx;
  if (lineRect.width === 0 && lineRect.height === 0) return caretLeftScreenPx;
  const contentLeft = Math.min(
    lineRect.left + paddingLeftCssPx * zoom,
    lineRect.left + Math.max(lineRect.width, 0)
  );
  return Math.max(caretLeftScreenPx, contentLeft);
}

/**
 * 切り詰め先の枠を決める。
 * テキスト図形は本文の実描画高さまで枠を伸ばし（他参加者の選択枠と同じ規則）、他は `object.h` を使う。
 */
export function resolveClampBox(
  object: { readonly type: string; readonly w: number; readonly h: number },
  renderedHeight: number | null
): ClampBox {
  const height =
    object.type === "text" && renderedHeight !== null && isPositiveFinite(renderedHeight)
      ? Math.max(object.h, renderedHeight)
      : object.h;
  return { width: object.w, height };
}
